<template>
  <AppLayout active-item="home">
    <AePageContent>
      <AeAlert v-if="err" type="error">{{ err }}</AeAlert>

      <section class="home-map-panel">
        <div class="home-map-header">
          <div>
            <h2>Mappa stazioni</h2>
            <p>{{ filteredStations.length }} stazioni su {{ stations.length }}</p>
          </div>

          <div class="home-map-header-actions">
            <AeButton variant="ghost" @click="filtersOpen = !filtersOpen">
              Filtri<span v-if="activeFiltersCount" class="home-filters-badge">{{ activeFiltersCount }}</span>
            </AeButton>
            <AeButton variant="primary" :loading="loading" @click="loadStations">
              Aggiorna
            </AeButton>
          </div>
        </div>

        <div v-if="filtersOpen" class="home-filters-panel">
          <label class="home-search">
            <span>Cerca</span>
            <input
              v-model.trim="homeMapState.search"
              class="home-search-input"
              type="search"
              placeholder="Stazione, id stazione, comune, provincia"
            />
          </label>
        </div>

        <div v-if="loading" class="home-map-loading">
          <div class="spinner-border"></div>
        </div>

        <div v-else class="home-map-layout">
          <AeMap
            ref="mapRef"
            class="home-map"
            :opt="mapOptions"
            :layers="stationLayers"
            @featureClick="onStationClick"
            @ready="onMapReady"
          />

          <aside class="home-station-list">
            <p v-if="!filteredStations.length" class="home-station-list-empty">Nessuna stazione trovata.</p>
            <button
              v-for="station in filteredStations"
              :key="station.idstazione"
              class="home-station-item"
              @click="goToStation(station)"
            >
              <strong>{{ station.nome || station.idstazione }}</strong>
              <small>#{{ station.idstazione }} · {{ station.comune || '-' }} ({{ station.provincia || '-' }})</small>
            </button>
          </aside>
        </div>
      </section>
    </AePageContent>
  </AppLayout>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAppStore } from '../stores/app.js'
import AppLayout from '../layouts/AppLayout.vue'
import { api } from '../services/api.js'
import { homeMapState } from '../composables/useHomeMapState.js'
import { AePageHeader, AePageContent, AeButton, AeAlert, AeMap } from '@aevue/components'

const appStore = useAppStore()
const router = useRouter()
const loading = ref(false)
const err = ref(null)
const stations = ref([])
const mapRef = ref(null)
const filtersOpen = ref(!!homeMapState.search)

const activeFiltersCount = computed(() => (homeMapState.search ? 1 : 0))

// Se torniamo da una navigazione precedente riprendiamo centro/zoom salvati
// e disattiviamo l'auto-fit, altrimenti il ricaricamento dei layer lo
// resetterebbe subito (vedi useLeafletMap: fitToLayers gira ad ogni cambio
// di `layers` finche' auto_fit non e' disattivato).
const mapOptions = {
  height: 'calc(100vh - 300px)',
  width: '100%',
  center: homeMapState.center || { lat: 43.5, lon: 11.2 },
  zoom: homeMapState.zoom || 7,
  show_scale: true,
  layer_selector: true,
  auto_fit: !homeMapState.center,
}

function onMapReady(map) {
  map.on('moveend zoomend', () => {
    const center = map.getCenter()
    homeMapState.center = { lat: center.lat, lon: center.lng }
    homeMapState.zoom = map.getZoom()
  })
}

const filteredStations = computed(() => {
  const q = homeMapState.search.toLowerCase()
  if (!q) return stations.value
  return stations.value.filter((station) => {
    const haystack = [station.nome, station.idstazione, station.comune, station.provincia]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return haystack.includes(q)
  })
})

const stationLayers = computed(() => {
  if (!filteredStations.value.length) return []

  return [{
    name: 'Stazioni',
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: filteredStations.value.map((station) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [Number(station.lon), Number(station.lat)],
        },
        properties: station,
      })),
    },
    options: {
      popup: false,
      pointToCircle: true,
      circleOptions: {
        radius: 6,
        color: '#0f766e',
        fillColor: '#14b8a6',
        fillOpacity: 0.75,
        weight: 1,
      },
    },
  }]
})

function goToStation(station) {
  if (!station?.idstazione) return
  router.push({ name: 'CommonStationDetailView', params: { idstazione: station.idstazione } })
}

function onStationClick(feature) {
  goToStation(feature?.properties)
}

async function loadStations() {
  loading.value = true
  err.value = null
  const res = await api.get('/api/v2/common/stazioni')
  loading.value = false

  if (!res.ok) {
    err.value = res.message || 'Errore caricamento stazioni'
    stations.value = []
    return
  }

  stations.value = res.data || []
}

onMounted(loadStations)

watch(() => appStore.isAuthenticated, (isAuthenticated) => {
  if (isAuthenticated && !stations.value.length) loadStations()
})
</script>

<style scoped>
.home-map-panel {
  display: grid;
  gap: 12px;
}

.home-map-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.home-map-header h2 {
  margin: 0;
  font-size: 20px;
  white-space: nowrap;
}

.home-map-header p {
  margin: 4px 0 0;
  color: var(--ae-text-muted, #667085);
  font-size: 13px;
  white-space: nowrap;
}

.home-map-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.home-filters-badge {
  display: inline-grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 6px;
  border-radius: 999px;
  background: var(--ae-primary-color, #1565c0);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
}

.home-filters-panel {
  border: 1px solid var(--ae-border-color, #d7dde8);
  border-radius: 8px;
  padding: 12px;
  background: var(--ae-surface-color, #fff);
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}

.home-search {
  display: grid;
  gap: 6px;
  flex: 1 1 auto;
  min-width: 200px;
  font-size: 13px;
}

.home-search-input {
  min-height: 38px;
  border: 1px solid var(--ae-border-color, #d7dde8);
  border-radius: 6px;
  padding: 8px 10px;
  background: var(--ae-surface-color, #fff);
  color: inherit;
}

.home-map-loading {
  display: grid;
  min-height: 360px;
  place-items: center;
}

.home-map-layout {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 12px;
  align-items: start;
}

.home-map {
  min-width: 0;
}

.home-station-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  border: 1px solid var(--ae-border-color, #d7dde8);
  border-radius: 8px;
  padding: 8px;
}

.home-station-list-empty {
  margin: 0;
  padding: 8px;
  color: var(--ae-text-muted, #667085);
  font-size: 13px;
}

.home-station-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
  border: 1px solid var(--ae-border-color, #e5e7eb);
  border-radius: 6px;
  padding: 8px 10px;
  background: var(--ae-surface-color, #fff);
  cursor: pointer;
  font: inherit;
}

.home-station-item:hover {
  border-color: #1565c0;
}

.home-station-item small {
  color: var(--ae-text-muted, #667085);
  font-size: 11.5px;
}

@media (max-width: 900px) {
  .home-map-layout {
    grid-template-columns: 1fr;
  }

  .home-station-list {
    max-height: 320px;
  }
}
</style>
